import { useState } from "react";
import { generateRouteModesMusicStream } from "@services/ai/aiApi";
import { isDirectionPoint } from "@utils/mode_tool/routePointAutomation";
import { normalizeRouteMusicByLanguage } from "@utils/mode_tool/modeRoute";
import { resolveMediaKind, resolveMediaUrl } from "@utils/media/mediaUrl";

function readEnglishText(field) {
  if (typeof field === "string") return field.trim();
  if (Array.isArray(field)) {
    const entry = field.find((item) =>
      Object.keys(item || {}).some((key) => key.toLowerCase() === "en"),
    );
    const key = Object.keys(entry || {}).find(
      (itemKey) => itemKey.toLowerCase() === "en",
    );
    const value = entry?.[key];
    return String(value?.value ?? value ?? "").trim();
  }
  return String(field?.en?.value ?? field?.en ?? field?.value ?? "").trim();
}

function readLocalizedText(field, languageIso) {
  const language = String(languageIso || "")
    .toLowerCase()
    .trim();
  if (typeof field === "string") return language === "en" ? field.trim() : "";
  if (Array.isArray(field)) {
    const entry = field.find((item) =>
      Object.keys(item || {}).some((key) => key.toLowerCase() === language),
    );
    const key = Object.keys(entry || {}).find(
      (itemKey) => itemKey.toLowerCase() === language,
    );
    const value = entry?.[key];
    return String(value?.value ?? value ?? "").trim();
  }
  const localizedValue = field?.[language];
  return String(localizedValue?.value ?? localizedValue ?? "").trim();
}

function readDuration(media) {
  const duration = Number(
    media?.durationSeconds ?? media?.duration_seconds ?? media?.duration,
  );
  return Number.isFinite(duration) && duration > 0 ? Math.round(duration) : 0;
}

function findFirstPointAudio(point = {}, languageIso = "") {
  const audios = Array.isArray(point?.audios) ? point.audios : [];
  const multimediaAudios = (
    Array.isArray(point?.multimedia) ? point.multimedia : []
  ).filter((item) => resolveMediaKind(item) === "audio");
  const availableAudios = [...multimediaAudios, ...audios].filter(
    (item, index, items) =>
      items.findIndex(
        (candidate) =>
          String(
            candidate?.id || candidate?.sourceId || candidate?.url || "",
          ) === String(item?.id || item?.sourceId || item?.url || ""),
      ) === index,
  );
  const language = String(languageIso || "")
    .toLowerCase()
    .trim();
  return (
    availableAudios.find(
      (item) =>
        String(item?.language || item?.languageIso || "")
          .toLowerCase()
          .trim() === language,
    ) || null
  );
}

function readAudioMetadataDuration(url) {
  return new Promise((resolve, reject) => {
    if (typeof Audio === "undefined" || !url) {
      reject(new Error("The audio duration is unavailable."));
      return;
    }
    const audio = new Audio();
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("Could not read the audio duration."));
    }, 15000);
    const cleanup = () => {
      window.clearTimeout(timeoutId);
      audio.onloadedmetadata = null;
      audio.onerror = null;
      audio.removeAttribute("src");
      audio.load();
    };
    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      const duration = Number(audio.duration);
      cleanup();
      if (!Number.isFinite(duration) || duration <= 0) {
        reject(new Error("The audio has no readable duration."));
        return;
      }
      resolve(Math.round(duration));
    };
    audio.onerror = () => {
      cleanup();
      reject(new Error("Could not read the audio duration."));
    };
    audio.src = url;
  });
}

async function getAudioDuration(point, languageIso) {
  const audio = findFirstPointAudio(point, languageIso);
  if (!audio)
    throw new Error(
      `This point has no ${String(languageIso || "").toUpperCase()} audio in Audios.`,
    );
  const storedDuration = readDuration(audio);
  const duration =
    storedDuration ||
    (await readAudioMetadataDuration(
      resolveMediaUrl(String(audio?.url || audio?.src || "")),
    ));
  if (duration < 5 || duration > 600) {
    throw new Error(
      "The selected voice audio must be between 5 and 600 seconds long.",
    );
  }
  return duration;
}

function getPointLabel(point, index) {
  const name = readEnglishText(point?.name);
  return `#${point?.number_point ?? point?.order ?? index + 1}${name ? ` · ${name}` : ""}`;
}

function isDirectionMusicPoint(point = {}) {
  return isDirectionPoint(point);
}

function useRouteMusicGeneration({
  tourId,
  routeType,
  modePoints,
  languageIso,
  onCommitPoint,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [pointScope, setPointScope] = useState("all");
  const [targetPoint, setTargetPoint] = useState(null);
  const [promptSingle, setPromptSingle] = useState("");
  const [selectedLanguageIso, setSelectedLanguageIso] = useState(
    String(languageIso || "en").toLowerCase(),
  );
  const [error, setError] = useState("");
  const [progress, setProgress] = useState({
    total: 0,
    completed: 0,
    points: [],
    done: false,
    completeReceived: false,
  });

  function open() {
    if (isGenerating) return;
    setTargetPoint(null);
    setPointScope("all");
    setPromptSingle("");
    setSelectedLanguageIso(String(languageIso || "en").toLowerCase());
    setError("");
    setProgress({
      total: 0,
      completed: 0,
      points: [],
      done: false,
      completeReceived: false,
    });
    setIsOpen(true);
  }

  function openForPoint(point, pointLanguageIso = languageIso) {
    if (isGenerating || !point?.id) return;
    setTargetPoint(point);
    setPromptSingle("");
    setSelectedLanguageIso(String(pointLanguageIso || "en").toLowerCase());
    setError("");
    setProgress({
      total: 0,
      completed: 0,
      points: [],
      done: false,
      completeReceived: false,
    });
    setIsOpen(true);
  }

  function close() {
    if (!isGenerating) setIsOpen(false);
  }

  async function generate() {
    const allPoints = Array.isArray(modePoints) ? modePoints : [];
    const targetIndex = targetPoint
      ? allPoints.findIndex(
          (point) => String(point?.id || "") === String(targetPoint.id),
        )
      : -1;
    const selectedPoints = targetPoint
      ? [{ point: targetPoint, index: Math.max(targetIndex, 0) }]
      : allPoints
          .map((point, index) => ({ point, index }))
          .filter(
            ({ point }) =>
              pointScope === "all" ||
              (pointScope === "directions"
                ? isDirectionMusicPoint(point)
                : !isDirectionMusicPoint(point)),
          );
    if (!String(tourId || "").trim()) {
      setError(
        "The tour ID is unavailable. Select a tour before generating music.",
      );
      return;
    }
    if (
      !["driving", "walking", "random"].includes(
        String(routeType || "").toLowerCase(),
      )
    ) {
      setError(
        "AI Music Background is available for Driving, Walking, and Random routes.",
      );
      return;
    }
    if (!selectedPoints.length || selectedPoints.length > 500) {
      setError(
        "The selected scope must contain between 1 and 500 route points.",
      );
      return;
    }
    if (typeof onCommitPoint !== "function") {
      setError("Generated music cannot be applied to the route right now.");
      return;
    }
    const requestLanguage = String(selectedLanguageIso || "")
      .toLowerCase()
      .trim();
    if (!requestLanguage) {
      setError("Select a language before generating music.");
      return;
    }

    const completedIds = new Set();
    setIsGenerating(true);
    setError("");
    setProgress({
      total: selectedPoints.length,
      completed: 0,
      done: false,
      completeReceived: false,
      points: selectedPoints.map(({ point, index }) => ({
        index,
        id: String(point?.id || ""),
        label: getPointLabel(point, index),
        status: "pending",
      })),
    });

    const updatePointStatus = (pointId, status, message = "") => {
      setProgress((current) => {
        const nextPoints = current.points.map((item) =>
          item.id === pointId ? { ...item, status, message } : item,
        );
        return {
          ...current,
          points: nextPoints,
          completed: nextPoints.filter((item) => item.status !== "pending")
            .length,
        };
      });
    };

    const applyMusicResult = (eventData) => {
      const row = eventData?.point ?? eventData?.data ?? eventData;
      const pointId = String(eventData?.id || row?.id || "");
      const source = selectedPoints.find(
        ({ point }) => String(point?.id || "") === pointId,
      )?.point;
      const resultLanguage = String(
        eventData?.language || row?.language || requestLanguage,
      )
        .toLowerCase()
        .trim();
      const completionKey = `${pointId}:${resultLanguage}`;
      if (!source || completedIds.has(completionKey)) return;
      const status = String(
        eventData?.status || (row?.error ? "error" : "completed"),
      );
      const music = row?.music;
      if (
        status === "error" ||
        row?.error === true ||
        !music?.id ||
        !music?.url
      ) {
        completedIds.add(completionKey);
        updatePointStatus(
          pointId,
          "error",
          String(
            eventData?.message || row?.message || "Music generation failed.",
          ),
        );
        return;
      }
      const normalizedMusic = {
        ...music,
        id: String(music.id),
        url: resolveMediaUrl(String(music.url)),
        duration_seconds: Number(music.duration_seconds || 0),
        mime_type: String(music.mime_type || "audio/mpeg"),
        language: resultLanguage,
      };
      const nextMusic = [
        ...normalizeRouteMusicByLanguage(source?.music).filter(
          (item) => item.language !== resultLanguage,
        ),
        normalizedMusic,
      ];
      onCommitPoint(pointId, { ...source, music: nextMusic });
      completedIds.add(completionKey);
      updatePointStatus(pointId, "completed");
    };

    try {
      const preparedPoints = await Promise.all(
        selectedPoints.map(async ({ point, index }) => {
          const id = String(point?.id || "");
          try {
            if (!id) throw new Error("This route point has no ID.");
            const description = readLocalizedText(
              point?.description,
              requestLanguage,
            );
            if (!description) {
              throw new Error(
                `This point has no ${requestLanguage.toUpperCase()} description.`,
              );
            }
            const duration = await getAudioDuration(point, requestLanguage);
            return {
              id,
              language: requestLanguage,
              description,
              type: isDirectionMusicPoint(point) ? "direction" : "point",
              duration,
            };
          } catch (preparationError) {
            completedIds.add(id);
            updatePointStatus(
              id,
              "error",
              preparationError?.message || "Could not prepare this point.",
            );
            return null;
          }
        }),
      );
      const requestPoints = preparedPoints.filter(Boolean);
      if (!requestPoints.length) {
        setProgress((current) => ({
          ...current,
          done: true,
          completeReceived: true,
        }));
        return;
      }

      const response = await generateRouteModesMusicStream(
        {
          tour_id: String(tourId),
          prompt_single: String(promptSingle || "").trim(),
          points: requestPoints,
        },
        ({ event, data }) => {
          if (event === "start") {
            setProgress((current) => ({
              ...current,
              apiTotal: Number(data?.total) || requestPoints.length,
            }));
          } else if (event === "point") {
            applyMusicResult(data);
          } else if (event === "complete") {
            setProgress((current) => ({
              ...current,
              done: true,
              completeReceived: true,
            }));
          }
        },
      );
      const responsePoints = response?.body?.points;
      if (Array.isArray(responsePoints))
        responsePoints.forEach((row) =>
          applyMusicResult({
            id: row?.id,
            language: row?.language,
            point: row,
            status: row?.error ? "error" : "completed",
          }),
        );
      setProgress((current) => ({
        ...current,
        completed: current.points.length,
        done: true,
        completeReceived: true,
      }));
    } catch (generationError) {
      const message = generationError?.message || "Music generation failed.";
      setError(message);
      setProgress((current) => ({
        ...current,
        points: current.points.map((item) =>
          item.status === "pending"
            ? { ...item, status: "error", message }
            : item,
        ),
        completed: current.points.length,
        done: true,
      }));
    } finally {
      setIsGenerating(false);
    }
  }

  return {
    isOpen,
    isGenerating,
    pointScope,
    setPointScope,
    promptSingle,
    setPromptSingle,
    selectedLanguageIso,
    setSelectedLanguageIso,
    targetPointId: String(targetPoint?.id || ""),
    error,
    progress,
    open,
    openForPoint,
    close,
    generate,
  };
}

export default useRouteMusicGeneration;
